import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "../../../components/ui/Modal";
import { UserPicker } from "../../../components/ui/UserPicker";
import type { SelectedUser } from "../../../components/ui/UserPicker";
import { useAddBusinessMember, useBusinessMembers } from "../../../hooks/useBusinesses";
import { bizRoleApi } from "../../../lib/roleApi";

const INPUT_CLASS =
  "w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20";
const LABEL_CLASS = "block text-xs font-medium text-[#374151] mb-1";
const ERROR_CLASS = "text-[#dc2626] text-xs mt-1";

interface AddExistingMemberModalProps {
  businessId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddExistingMemberModal({
  businessId,
  isOpen,
  onClose,
  onSuccess,
}: AddExistingMemberModalProps) {
  const [selected, setSelected] = useState<SelectedUser | null>(null);
  const [roleId, setRoleId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addMember = useAddBusinessMember(businessId);

  const { data: rolesData } = useQuery({
    queryKey: ["biz-roles", businessId],
    queryFn: () => bizRoleApi.list(businessId),
    enabled: isOpen && !!businessId,
  });
  const roles = rolesData?.items ?? [];

  // Existing members of this business — hidden from the picker so you can't add a duplicate.
  const { data: membersData } = useBusinessMembers(isOpen ? businessId : "");
  const existingMemberIds = (membersData?.items ?? []).map((m) => m.userId);

  const reset = () => {
    setSelected(null);
    setRoleId("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selected) {
      setError("Please select an employee.");
      return;
    }
    setError(null);
    try {
      await addMember.mutateAsync({
        mode: "assign",
        userId: selected.userId,
        roleId: roleId || null,
        isBusinessAdmin: false,
      });
      reset();
      onSuccess();
    } catch {
      setError("Failed to grant access. The employee may already be a member of this business.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Grant Access to Existing Employee" size="md">
      <div className="space-y-4">
        <div>
          <label className={LABEL_CLASS}>Employee</label>
          <UserPicker
            value={selected}
            onChange={setSelected}
            placeholder="Search by name, username…"
            excludeUserIds={existingMemberIds}
          />
          <p className="text-[11px] text-[#94a3b8] mt-1">
            Searches employees not already in this business. The selected employee will gain access with the role below.
          </p>
        </div>

        <div>
          <label className={LABEL_CLASS}>
            Business Access Role <span className="text-[#94a3b8] font-normal">(optional)</span>
          </label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={INPUT_CLASS}>
            <option value="">— No role (basic employee access) —</option>
            {roles.map((r) => (
              <option key={r.roleId} value={r.roleId}>{r.name}</option>
            ))}
          </select>
          <p className="text-[11px] text-[#94a3b8] mt-1">
            Controls which pages this employee can access within this business.
          </p>
        </div>

        {error && <p className={ERROR_CLASS}>{error}</p>}

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={handleClose}
            disabled={addMember.isPending}
            className="rounded-lg border border-[#e2e8f0] px-4 py-2 text-sm font-medium text-[#374151] hover:bg-[#f8fafc] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={addMember.isPending || !selected}
            className="rounded-lg bg-[#2447d9] px-4 py-2 text-sm font-medium text-white hover:bg-[#1e3a8a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {addMember.isPending ? "Granting…" : "Grant Access"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
